import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Card } from '@/components/ui/Card'
import { validateEmail } from '@/utils/validation'
import { AppLogo } from '@/components/ui/AppLogo'
import { useBrandingStore } from '@/stores/branding.store'
import { Lock, Envelope } from 'phosphor-react'

export const LoginPage: React.FC = () => {
  const navigate = useNavigate()
  const { login } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [errors, setErrors] = useState<{ email?: string; password?: string; general?: string }>({})
  const [isLoading, setIsLoading] = useState(false)
  const appName = useBrandingStore(state => state.branding.appName)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})

    // Validation
    const newErrors: { email?: string; password?: string } = {}
    if (!email.trim()) {
      newErrors.email = "L'email est requis"
    } else if (!validateEmail(email)) {
      newErrors.email = 'Veuillez entrer une adresse email valide'
    }

    if (!password) {
      newErrors.password = 'Le mot de passe est requis'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setIsLoading(true)
    try {
      await login({ email: email.trim(), password })
      // Navigate to code validation page
      navigate('/login/verify', { state: { email: email.trim() } })
    } catch (error) {
      setErrors({
        general:
          error instanceof Error ? error.message : "Une erreur s'est produite. Veuillez réessayer.",
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[var(--color-gray-50)] via-white to-[var(--color-gray-100)] px-4 py-8 sm:py-12">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-10 sm:mb-12">
          <AppLogo size="lg" className="mx-auto mb-6 shadow-lg" />
          <h1 className="text-3xl sm:text-4xl font-bold text-[var(--color-text-primary)] mb-3">
            {appName}
          </h1>
          <p className="text-base sm:text-lg text-[var(--color-text-secondary)]">
            Connectez-vous à votre compte
          </p>
        </div>

        <Card padding="lg">
          <form onSubmit={handleSubmit} className="space-y-6">
            {errors.general && (
              <div
                className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm"
                role="alert"
              >
                {errors.general}
              </div>
            )}

            <Input
              label="Adresse email"
              type="email"
              placeholder="vous@exemple.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              error={errors.email}
              required
              autoComplete="email"
              autoFocus
              leftIcon={<Envelope className="w-5 h-5" weight="regular" />}
            />

            <Input
              label="Mot de passe"
              type="password"
              placeholder="Entrez votre mot de passe"
              value={password}
              onChange={e => setPassword(e.target.value)}
              error={errors.password}
              required
              autoComplete="current-password"
              leftIcon={<Lock className="w-5 h-5" weight="regular" />}
            />

            <div className="flex items-center justify-end pt-2">
              <button
                type="button"
                onClick={() => navigate('/login/forgot-password')}
                className="text-sm sm:text-base text-[var(--color-primary)] hover:text-[var(--color-primary-dark)] font-medium transition-colors"
              >
                Mot de passe oublié ?
              </button>
            </div>

            <div className="pt-2">
              <Button type="submit" fullWidth isLoading={isLoading} size="lg">
                Se connecter
              </Button>
            </div>
          </form>
        </Card>

        <p className="mt-8 text-center text-sm sm:text-base text-[var(--color-text-muted)]">
          Accès sécurisé à votre tableau de bord
        </p>
      </div>
    </div>
  )
}
